import React, { useState, useEffect } from 'react';
import { Grid, Card, Skeleton } from '@mui/material';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useSelector } from 'react-redux';
import { getRevenueImpactStats } from '../../../../api/overView';
import { useParams } from 'react-router';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  // Check if the date is in the format "YYYY-MM-DDTHH:mm:ss.sssZ"
  if (dateString.includes('T')) {
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).replace(/\//g, '-');
  }
  
  // If date is already in DD-MM-YYYY format, return as is
  if (dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
    return dateString;
  }
  
  // For any other format, try to parse and format
  const parts = dateString.split(/[-T]/);
  if (parts.length >= 3) {
    return `${parts[2].padStart(2, '0')}-${parts[1].padStart(2, '0')}-${parts[0]}`;
  }
  
  return dateString;
};

const ExpiredItem = ({ ean, expiryDate, mrp, loading }) => (
  <div className="bg-white rounded p-2 mb-2 text-sm shadow-sm">
    {loading ? (
      <>
        <Skeleton variant="text" width="60%" height={24} />
        <Skeleton variant="text" width="40%" height={20} />
        <Skeleton variant="text" width="30%" height={20} />
      </>
    ) : (
      <div className="flex items-start gap-2">
        <FiberManualRecordIcon 
          sx={{ 
            color: '#ef4444',
            fontSize: '12px',
            marginTop: '4px'
          }} 
        />
        <div>
          <div className="font-bold">EAN: {ean}</div>
          <div className="text-xs text-gray-500">
            Expires on: {expiryDate}
          </div>
          <div className="text-xs text-gray-500">
            MRP: {formatCurrency(mrp)}
          </div>
        </div>
      </div>
    )}
  </div>
);

const RevenueImpactCard = () => {
  const { storeId } = useParams(); 
  const [revenueStats, setRevenueStats] = useState({
    totalExpiredMrp: 0,
    expiredProducts: []
  });
  const [loading, setLoading] = useState(true);
  const selectedDate = useSelector((state) => state.customization.selectedDate);


  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await getRevenueImpactStats(selectedDate || new Date(), storeId, abortController.signal);
        
        if (!abortController.signal.aborted) {
          setRevenueStats({
            totalExpiredMrp: response.data?.total_expired_mrp || 0,
            expiredProducts: response.data?.data || []
          });
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching revenue impact data:', error);
          setRevenueStats({
            totalExpiredMrp: 0,
            expiredProducts: []
          });
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line
  }, [selectedDate]);

  return (
    <Grid item xs={12} sm={12} md={2.4} lg={2.4} xl={2.4}>
      <Card 
        className="border relative border-gray-300" 
        sx={{ 
          height: "276px",
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px'
        }}
      >
        <div className="flex w-full flex-col gap-1 p-3">
          <div className="flex items-center gap-2">
            <CurrencyRupeeIcon className="bg-emerald-500 text-white rounded-full p-2 text-6xl" />
            <div style={{ width: loading ? '120px' : 'auto' }}>
              {loading ? (
                <>
                  <Skeleton variant="text" width="100%" height={40} />
                  <Skeleton variant="text" width="80%" height={28} />
                </>
              ) : (
                <>
                  <p className="text-3xl">{formatCurrency(revenueStats.totalExpiredMrp)}</p>
                  <p className="text-lg font-semibold">Revenue Impact</p>
                </>
              )}
            </div>
          </div>
          <div className="bg-slate-100 flex-grow overflow-y-auto h-[184px] p-2 rounded-lg scrollbar">
            <div className="text-xs text-gray-500 mb-2 italic">
              Products at Risk
            </div>
            {loading ? (
              [...Array(3)].map((_, index) => (
                <div key={index} className="mb-2">
                  <div className="bg-white rounded p-2 text-sm shadow-sm">
                    <div className="flex items-start gap-2">
                      <Skeleton variant="circular" width={12} height={12} sx={{ marginTop: '4px' }} />
                      <div className="flex-1">
                        <Skeleton variant="text" width="60%" height={24} />
                        <Skeleton variant="text" width="40%" height={20} />
                        <Skeleton variant="text" width="30%" height={20} />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : revenueStats.expiredProducts.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-4">
                No products at risk
              </div>
            ) : (
              revenueStats.expiredProducts.map((item, index) => (
                <ExpiredItem
                  key={index}
                  ean={item.expired_products[0].ean}
                  expiryDate={formatDate(item.expired_products[0].expiry_date)}
                  mrp={item.expired_products[0].mrp}
                />
              ))
            )}
          </div>
        </div>
      </Card>
    </Grid>
  );
};

export default RevenueImpactCard;
